import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import type { Campaign, InvestmentType } from '@mobi/betslip/types'
import {
  selectBonusCashCampaigns,
  selectWorkflowStatus,
} from '@mobi/betslip/Store/Workflow/selectors'
import { toMoney } from '@mobi/utils/money'
import { setActiveInvestment } from '@mobi/betslip/Store/Workflow'
import { setSelectedCampaignForItem, setSelectedCampaignForMulti } from '@mobi/betslip/Store/Bets'
import { Price } from '../Price'

const enum LocalConstants {
  StackedDisplayClass = 'js-investment--stacked',
  BonusNotAppliedWithReadyState = 'js-investment__bonusnotapplied--readystate',
  BonusNotApplied = 'js-investment__bonusnotapplied',
  BonusBetClass = 'js-investment__bonusbet',
  BonusBetConfirmState = 'js-bonusbet--confirm',
  MultiInvestmentDisplayClass = 'js-multiinvestment--display',
}

export const BonusBetInvestment: React.FC<{
  id: string
  value: number
  investmentType: InvestmentType
  isEditable: boolean
  label?: string
  display?: 'inline' | 'stacked'
  price?: number
  priceBoost?: number
  selectedCampaign?: Campaign | undefined
}> = ({
  id,
  value,
  investmentType,
  label,
  display = 'inline',
  isEditable,
  price,
  priceBoost,
  selectedCampaign,
}) => {
  const dispatch = useDispatch()

  const workflowStatus = useAppSelector(selectWorkflowStatus)
  const bonusCashCampaigns = useSelector(selectBonusCashCampaigns)

  if (workflowStatus !== 'ready' && value === 0) {
    return null
  }

  const wrapperClassNames = []
  if (display === 'stacked' && isEditable) {
    wrapperClassNames.push(LocalConstants.StackedDisplayClass)
  }
  if (investmentType === 'Multi') {
    wrapperClassNames.push(LocalConstants.MultiInvestmentDisplayClass)
  }

  const clearBonusBetInvestment = () => {
    dispatch(setActiveInvestment(null))
    if (investmentType === 'Multi') {
      dispatch(setSelectedCampaignForMulti({ campaign: selectedCampaign }))
    } else {
      dispatch(setSelectedCampaignForItem({ id, campaign: selectedCampaign, bonusCashCampaigns }))
    }
  }

  return (
    <WrapperStyled className={wrapperClassNames.join(' ')}>
      <span>
        <span> {label || investmentType}</span>
        {price && <Price value={price} isIndicatorShifted priceBoost={priceBoost} />}
      </span>

      {value > 0 ? (
        workflowStatus !== 'ready' ? (
          <span className={LocalConstants.BonusBetConfirmState}>
            Bonus Bet {`${toMoney(value, { decimalPlaces: 2 })} `}
          </span>
        ) : (
          <span className={LocalConstants.BonusBetClass}>
            <span>${value} Bonus Bet</span>

            <span onClick={clearBonusBetInvestment}>
              <Icon name='SolidXClose' size='1.2rem' />
            </span>
          </span>
        )
      ) : (
        <span
          className={
            workflowStatus !== 'ready'
              ? LocalConstants.BonusNotApplied
              : LocalConstants.BonusNotAppliedWithReadyState
          }
        >
          <span>{toMoney(value, { decimalPlaces: 2 })}</span>
        </span>
      )}
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spacing.smx2,
  scrollMargin: spacing.sm,
  minHeight: '2.4rem',

  // Label and Price Change Wrapper
  '> span:first-of-type': {
    display: 'inline-flex',
    gap: spacing.smx1,

    // Label
    '> span:first-of-type': {
      fontSize: font.size.md.fontSize,
      letterSpacing: font.size.md.letterSpacing,
      lineHeight: font.size.md.lineHeight,
      fontWeight: font.weight.regular,
      color: colors.black,

      ':empty': { display: 'none' },
    },

    '> span:nth-of-type(2)': {
      fontSize: font.size.md.fontSize,
      letterSpacing: font.size.md.letterSpacing,
      lineHeight: font.size.md.lineHeight,
      fontWeight: font.weight.medium,
      color: colors.neutral[900],
    },
  },

  '> span:last-of-type': {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: '13rem',
    minWidth: '13rem',
    textAlign: 'start',
    color: colors.neutral[700],
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,
    fontWeight: font.weight.regular,

    [`&.${LocalConstants.BonusNotAppliedWithReadyState}`]: {
      padding: spacing.sm + ' ' + spacing.md,
      border: '1px solid ' + colors.neutral[300],
      borderRadius: radius.md,
      backgroundColor: colors.white,
      boxShadow: shadow.xs,
      touchAction: 'manipulation',
      background: colors.surface[100],
      color: colors.neutral[400],
      pointerEvents: 'none',
      cursor: 'not-allowed',
    },

    [`&.${LocalConstants.BonusBetClass}`]: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: font.size.lg.fontSize,
      lineHeight: font.size.lg.lineHeight,
      height: '2.8rem',
      background: colors.success[50],
      color: colors.success[600],
      width: 'auto',
      padding: '0.4rem 1rem 0.4rem 0.8rem',
      borderRadius: radius.md,
      maxWidth: '100%',

      '> span:nth-of-type(1)': {
        paddingRight: '0.6rem',
      },

      '> span:nth-of-type(2)': {
        display: 'flex',
      },
    },

    [`&.${LocalConstants.BonusBetConfirmState}`]: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: font.size.md.fontSize,
      lineHeight: font.size.md.lineHeight,
      fontWeight: font.weight.medium,
      color: colors.success[600],
      background: colors.celery[50],
      padding: `0 ${spacing.smx2}`,
      width: 'auto',
      maxWidth: '100%',
      minWidth: 'auto',
      borderRadius: spacing.smx2,
    },

    [`&.${LocalConstants.BonusNotApplied}`]: {
      '> span': {
        color: colors.black,
        fontWeight: font.weight.medium,
      },
    },
  },

  [`&.${LocalConstants.StackedDisplayClass}`]: {
    flex: 1,
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'start',
    justifyContent: 'start',

    '> span:last-of-type': {
      maxWidth: 'unset',
    },
  },

  [`&.${LocalConstants.MultiInvestmentDisplayClass}`]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
})
